import { $fetch, type FetchOptions } from 'ofetch'

import { useHelpers } from './useHelpers'
import { useRouter } from 'vue-router'

type RequestBodyT =
  | {
      [key: string | number]: unknown
    }
  | FormData
  | Record<string, any>

type RequestHeadersT = Record<string, any> & Headers

const baseAPIFactory = (config: FetchOptions) => {
  const { VITE_API_BASE_URL: baseURL } = import.meta.env

  const defaultConfig: any = {
    baseURL,
    headers: {
      'Content-Type': 'application/json'
    },
    ...config
  }

  function get<ResponseType = any, OverwriteType = void>(url: string, options?: FetchOptions) {
    return $fetch<OverwriteType extends void ? ApiResponseT<ResponseType> : OverwriteType>(url, {
      ...defaultConfig,
      method: 'get',
      baseURL,
      headers: {
        'Content-Type': 'application/json'
      },
      ...options
    })
  }

  function post<ResponseType = any, OverwriteType = void>(url: string, data: RequestBodyT, options?: FetchOptions) {
    return $fetch<OverwriteType extends void ? ApiResponseT<ResponseType> : OverwriteType>(url, {
      ...defaultConfig,
      ...options,
      body: data,
      method: 'post'
    })
  }

  function put<ResponseType = any, OverwriteType = void>(url: string, data: RequestBodyT, options?: FetchOptions) {
    return $fetch<OverwriteType extends void ? ApiResponseT<ResponseType> : OverwriteType>(url, {
      ...defaultConfig,
      ...options,
      body: data,
      method: 'put'
    })
  }

  function patch<ResponseType = any, OverwriteType = void>(url: string, data: RequestBodyT, options?: FetchOptions) {
    return $fetch<OverwriteType extends void ? ApiResponseT<ResponseType> : OverwriteType>(url, {
      ...defaultConfig,
      ...options,
      body: data,
      method: 'patch'
    })
  }

  function del<ResponseType = any, OverwriteType = void>(url: string, options?: FetchOptions) {
    return $fetch<OverwriteType extends void ? ApiResponseT<ResponseType> : OverwriteType>(url, {
      ...defaultConfig,
      ...options,
      method: 'delete'
    })
  }

  return {
    post,
    get,
    put,
    patch,
    delete: del
  }
}

const useApiFactory = () => {
  const { getCookie } = useHelpers()
  const accessToken = getCookie('access_token')
  const router = useRouter()

  const authConfig: FetchOptions = {
    async onRequest({ options }) {
      options.headers = {
        ...options.headers,
        ...(accessToken && { Authorization: `Bearer ${accessToken}` })
      } as RequestHeadersT
    },

    async onResponse(context) {
      if (context.response.status == 401 || context.response.status == 403) {
        if ('push' in router) router.push('/auth')
      }
    }
  }

  return baseAPIFactory(authConfig)
}

const useAuthApiFactory = () => {
  const authConfig: FetchOptions = {
    async onRequest({ options }) {
      options.headers = {
        ...options.headers
      } as RequestHeadersT
    }
  }

  return baseAPIFactory(authConfig)
}

export default useApiFactory
export { useAuthApiFactory }
